import { useState, useEffect, useRef } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/pages/insurance/claim-detail/ require four levels up to reach convex/_generated/
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { toast } from 'sonner';
import type { TClaimStatus } from '@/@types/claims';

interface IUseClaimDetailActionsProps {
  claimId: Id<'claims'> | undefined;
  claimStatus: TClaimStatus | undefined;
  internalNotes: string | undefined;
}

export function useClaimDetailActions({
  claimId,
  claimStatus,
  internalNotes,
}: IUseClaimDetailActionsProps) {
  const updateStatus = useMutation(api.insurance.claims.updateStatus);
  const updateLineItem = useMutation(api.insurance.claims.updateLineItem);
  const recordPaymentMutation = useMutation(api.insurance.claims.recordPayment);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (internalNotes !== undefined && !initializedRef.current) {
      setNotes(internalNotes ?? '');
      initializedRef.current = true;
    }
  }, [internalNotes]);

  async function handleStatusChange(next: string) {
    if (!claimId) return;
    setIsUpdatingStatus(true);
    try {
      await updateStatus({ claimId, status: next as TClaimStatus });
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleSaveNotes() {
    if (!claimId || !claimStatus) return;
    setIsSavingNotes(true);
    try {
      await updateStatus({ claimId, status: claimStatus, internalNotes: notes });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  }

  async function handleUpdateLineItem(args: Parameters<typeof updateLineItem>[0]) {
    await updateLineItem(args);
  }

  async function handleRecordPayment(lineItemId: Id<'claimLineItems'>, amountPaid: number) {
    await recordPaymentMutation({ lineItemId, amountPaid });
  }

  return {
    notes,
    setNotes,
    isUpdatingStatus,
    isSavingNotes,
    handleStatusChange,
    handleSaveNotes,
    handleUpdateLineItem,
    handleRecordPayment,
  };
}
